import React from "react";
import { Form, message, Upload } from "antd";
import { useAppDispatch } from "redux/hooks";
import { createCoverPage } from "redux/slice/coverPageSlice";
import { Button, Input, Modal } from "components/ui-component";
import { UploadOutlined } from "@ant-design/icons";
import RemoveIcons from "components/Icons/RemoveIcon";

interface AddCoverPageModalProps {
  visible: boolean;
  onCancelClick: () => void;
  onSuccess: () => void;
}

const AddCoverPageModal = ({
  visible,
  onCancelClick,
  onSuccess,
}: AddCoverPageModalProps) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [fileList, setFileList] = React.useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleClose = () => {
    form.resetFields();
    setFileList([]);
    onCancelClick();
  };

  const handleRemove = () => {
    setFileList([]);
    form.setFieldsValue({ cover_file: undefined });
  };

  const beforeUpload = (file: File) => {
    // Validate file type
    const isAllowedType =
      file.type === "application/pdf" ||
      file.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    if (!isAllowedType) {
      message.error("Only PDF and DOCX files are allowed");
      return Upload.LIST_IGNORE;
    }
    // Validate file size
    if (file.size > 5242880) {
      message.error("File size is too large (max 5MB)");
      return Upload.LIST_IGNORE;
    }
    setFileList([file]);
    form.setFieldsValue({ cover_file: file });
    return false; // Prevent auto upload
  };

  const onFinish = async (values: any) => {
    setIsSubmitting(true);
    try {
      const file = fileList[0];
      if (!file) {
        message.error("Please select a file");
        setIsSubmitting(false);
        return;
      }
      const formData = new FormData();
      formData.append("cover_file", file);
      formData.append("name", values.name);
      formData.append("description", values.description);

      await dispatch(createCoverPage(formData)).unwrap();
      message.success("Cover page added successfully");
      onSuccess();
      handleClose();
    } catch (error: any) {
      message.error(error?.message || "Failed to add cover page");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title="Add Cover Page"
      open={visible}
      onCancel={handleClose}
      onOk={() => form.submit()}
      destroyOnClose
      centered
      closable={false}
      cancelText="Cancel"
      okText="Add Cover Page"
      isLoading={isSubmitting}
      className="add-cover-page-modal"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ name: "", description: "" }}
      >
        <Form.Item
          label="Cover Page Name"
          name="name"
          rules={[
            { required: true, message: "Cover page name is required" },
            { max: 255, message: "Name must be at most 255 characters" },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Cover File"
          name="cover_file"
          rules={[
            {
              required: true,
              message: "Cover file is required",
            },
          ]}
        >
          <Upload
            beforeUpload={beforeUpload}
            fileList={fileList}
            maxCount={1}
            accept=".pdf,.docx"
            className="upload-wrapper"
            showUploadList={{
              showRemoveIcon: true,
              removeIcon: <RemoveIcons />,
            }}
            onRemove={handleRemove}
          >
            <Button size="small" type="primary" icon={<UploadOutlined />}>
              Select File
            </Button>
          </Upload>
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
          rules={[
            { required: true, message: "Description is required" },
            { max: 500, message: "Description must be at most 500 characters" },
          ]}
        >
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddCoverPageModal;
